import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Row, Col } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { Sidebar } from '../components/Sidebar';
import { Header } from '../components/header';
import { EmployerInfoSection } from '../components/employment-contract/employer-info-section.component';
import { EmployeeInfoSection } from '../components/employment-contract/employee-info-section.component';
import { EmployerSignature } from '../components/employment-contract/employer-signature.component';
import { InvozioSignature } from '../components/employment-contract/invozio-signature.component';
import { fetchPersonalData } from '../features/profile-settings/profile-settings.slice';
import { createEmploymentContract, resetEmploymentContractState } from '../features/employment-contract/employment-contract.slice';
import '../scss/employment-contract.scss';

function generateContractNumber(): string {
  const now = new Date();
  const dd = String(now.getDate()).padStart(2, '0');
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const yyyy = now.getFullYear();
  return `EC_${dd}${mm}${yyyy}_001`;
}

function EmploymentContractPage() {
  const { t, i18n } = useTranslation();

  // Explicit language-change subscription to ensure React 18 re-renders
  // react-i18next v11 has batching gaps with React 18 createRoot
  const [, setLangTick] = useState(0);
  useEffect(() => {
    const handler = () => setLangTick((n) => n + 1);
    i18n.on('languageChanged', handler);
    return () => { i18n.off('languageChanged', handler); };
  }, [i18n]);
  const navigate = useNavigate();
  const dispatch = useDispatch<any>();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const lang = new URLSearchParams(window.location.search).get('lang') || 'en-us';

  const user = useSelector((state: any) => state.auth.user);
  const personalData = useSelector((state: any) => state.profileSettings.personalData);
  const { isLoading, isSuccess, isError, message } = useSelector(
    (state: any) => state.employmentContract
  );

  const contractNumber = generateContractNumber();

  // Employer signature state
  const [employerDate, setEmployerDate] = useState('');
  const [employerSignature, setEmployerSignature] = useState<string | null>(null);
  const [employerName, setEmployerName] = useState('');
  const [isDigitally, setIsDigitally] = useState(false);
  const [uploadedFileName, setUploadedFileName] = useState('');

  // Validation errors
  const [errors, setErrors] = useState<{ date?: string; signature?: string; name?: string }>({});

  useEffect(() => {
    if (user?.userId) {
      dispatch(fetchPersonalData(String(user.userId)));
    }
  }, [dispatch, user?.userId]);

  // Redirect on success
  useEffect(() => {
    if (isSuccess) {
      toast.success(String(t('CONTRACT_SUBMITTED_SUCCESS') || 'Employment contract submitted successfully!'));
      dispatch(resetEmploymentContractState());
      navigate(`/v2/my-contracts?lang=${lang}`);
    }
  }, [isSuccess, dispatch, navigate, lang, t]);

  // Show API error
  useEffect(() => {
    if (isError && message) {
      toast.error(message);
      dispatch(resetEmploymentContractState());
    }
  }, [isError, message, dispatch]);

  const handleCancel = () => {
    navigate(`/v2/my-contracts?lang=${lang}`);
  };

  const handleSubmit = () => {
    const newErrors: { date?: string; signature?: string; name?: string } = {};

    if (!employerDate) {
      newErrors.date = 'EC_VALIDATION_DATE_REQUIRED';
    }
    if (!employerSignature) {
      newErrors.signature = 'EC_VALIDATION_SIGNATURE_REQUIRED';
    }
    if (!employerName.trim()) {
      newErrors.name = 'EC_VALIDATION_NAME_CLARIFICATION_REQUIRED';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});

    // Build ISO date string from date input value
    const signatureDate = new Date(employerDate).toISOString();

    // Extract base64 content from dataUrl (strip "data:image/...;base64," prefix)
    const base64Content = employerSignature
      ? employerSignature.replace(/^data:image\/[a-z]+;base64,/, '')
      : '';

    const fileName = isDigitally
      ? (uploadedFileName || 'signature.png')
      : 'signature-drawn.png';

    dispatch(
      createEmploymentContract({
        employeeSignatureDate: signatureDate,
        employeeNameClarification: employerName.trim(),
        employeeSignatureFileName: fileName,
        employeeSignatureFileContent: base64Content,
        isDigitally,
      })
    );
  };

  return (
    <div className="dashboard-page">
      <div className="dashboard-layout">
        <Sidebar
          activeItemId="contracts"
          footer={null}
          onClose={() => setIsSidebarOpen(false)}
          className={`dashboard-sidebar ${isSidebarOpen ? 'sidebar-open' : ''}`}
        />
        {isSidebarOpen && (
          <div className="sidebar-overlay" onClick={() => setIsSidebarOpen(false)} />
        )}

        <div className="dashboard-main-content">
          <Header setIsSidebarOpen={setIsSidebarOpen} />

          <div className="ec-page-wrapper">
            {/* Page Header */}
            <div className="ec-page-header">
              <h2 className="ec-page-title">{t('CREATE_EMPLOYMENT_CONTRACT') || 'Create Employment Contract'}</h2>
              {/* <span className="ec-contract-no-badge">{contractNumber}</span> */}
            </div>

            {/* Employer Information */}
            <EmployerInfoSection />

            {/* Employee Information */}
            <EmployeeInfoSection personalData={personalData} />

            {/* Signatures */}
            <div className="ec-section-card">
              <h3 className="ec-section-title">{t('SIGNATURES') || 'Signatures'}</h3>
              <Row>
                <Col md={6}>
                  <EmployerSignature
                    date={employerDate}
                    onDateChange={(v) => { setEmployerDate(v); setErrors((e) => ({ ...e, date: undefined })); }}
                    signatureDataUrl={employerSignature}
                    onSignatureChange={(v) => { setEmployerSignature(v); setErrors((e) => ({ ...e, signature: undefined })); }}
                    nameClarification={employerName}
                    onNameClarificationChange={(v) => { setEmployerName(v); setErrors((e) => ({ ...e, name: undefined })); }}
                    onSignatureModeChange={setIsDigitally}
                    uploadedFileName={uploadedFileName}
                    onUploadedFileNameChange={setUploadedFileName}
                    errors={errors}
                  />
                </Col>
                <Col md={6}>
                  <InvozioSignature
                    employerDate={employerDate}
                    employerSignatureDataUrl={employerSignature}
                    employerNameClarification={employerName}
                  />
                </Col>
              </Row>
            </div>

            {/* Footer Actions */}
            <div className="ec-footer-actions">
              <button type="button" className="ec-btn ec-btn--cancel" onClick={handleCancel}>
                {t('CANCEL') || 'Cancel'}
              </button>
              <button type="button" className="ec-btn ec-btn--submit" onClick={handleSubmit} disabled={isLoading}>
                {isLoading ? (t('LOADING') || 'Loading...') : (t('SUBMIT') || 'Submit')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EmploymentContractPage;
